import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { sdsRadius } from '../theme/solutionsDesignSystem';

const useStyles = makeStyles(() => ({
    zoomContainer: {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden', // Clips the slight zoom in-place
        borderRadius: sdsRadius.sm,
        maxWidth: '100%',
        maxHeight: '100%',
        cursor: 'pointer', // "click" hand icon on hover
        userSelect: 'none',
        '&:hover $innerWrapper': {
            transform: 'scale(1.06)', // slight zoom in-place
        },
    },
    innerWrapper: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        transition: 'transform 300ms cubic-bezier(0.16, 1, 0.3, 1)',
        willChange: 'transform',
        transformOrigin: 'center center',
    },
    modalOverlay: {
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        cursor: 'zoom-out',
        background: 'rgba(43, 44, 46, 0.85)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        animation: '$fadeIn 180ms ease-out',
    },
    modalContent: {
        position: 'relative',
        maxWidth: 'min(92vw, 1100px)',
        maxHeight: 'min(90vh, 900px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        outline: 'none',
        cursor: 'default',
    },
    modalImg: {
        maxWidth: '100%',
        maxHeight: 'min(84vh, 840px)',
        width: 'auto',
        height: 'auto',
        objectFit: 'contain',
        borderRadius: sdsRadius.sm,
        boxShadow: '0 24px 48px rgba(0, 0, 0, 0.45)',
        background: '#000',
    },
    modalCaption: {
        marginTop: 12,
        color: 'rgba(255, 255, 255, 0.85)',
        fontSize: 14,
        textAlign: 'center',
        maxWidth: '80%',
    },
    closeBtn: {
        position: 'fixed',
        top: 24,
        right: 24,
        width: 40,
        height: 40,
        borderRadius: sdsRadius.sm,
        border: 0,
        background: '#FFFFFF',
        color: '#2B2C2E',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
        transition: 'transform 120ms ease, background 120ms ease',
        '&:hover': {
            background: '#F4EEEE',
            transform: 'scale(1.05)',
        },
    },
    '@keyframes fadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
    },
}));

const CloseIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <path d="M6 6l12 12M18 6L6 18" />
    </svg>
);

/**
 * PbdqImageLightboxWrapper
 * 
 * - On hover: slightly zooms in place (center-aligned, cropped cleanly by container overflow).
 * - Cursor: 'pointer' (click hand icon).
 * - On click: opens the full scale definition modal.
 */
const GENERIC_CAPTIONS = new Set([
    'image preview',
    'preview',
    'photo',
    'feature photo',
    'featured photo',
    'image',
    'thumbnail',
]);

const PbdqImageLightboxWrapper = ({
    src,
    alt,
    caption,
    className,
    children,
}) => {
    const classes = useStyles();
    const [isOpen, setIsOpen] = useState(false);

    const handleOpen = () => setIsOpen(true);
    const handleClose = () => setIsOpen(false);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            handleClose();
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.removeEventListener('keydown', handleKeyDown);
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [isOpen, handleKeyDown]);

    const imageSrc = src || (children && children.props && children.props.src);
    const imageAlt = alt || (children && children.props && children.props.alt) || 'Image preview';
    const rawCaption = caption !== undefined ? caption : imageAlt;
    const isGenericCaption = !rawCaption || GENERIC_CAPTIONS.has(String(rawCaption).trim().toLowerCase());
    const displayCaption = isGenericCaption ? null : rawCaption;

    return (
        <>
            <div
                role="button"
                tabIndex={0}
                aria-label={`Inspect ${imageAlt}`}
                onClick={handleOpen}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOpen();
                    }
                }}
                className={clsx(classes.zoomContainer, className)}
            >
                <div className={classes.innerWrapper}>
                    {children}
                </div>
            </div>

            {isOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label={imageAlt}
                    className={classes.modalOverlay}
                    onClick={handleClose}
                >
                    <div
                        className={classes.modalContent}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={imageSrc}
                            alt={imageAlt}
                            className={classes.modalImg}
                        />
                        {displayCaption && (
                            <div className={classes.modalCaption}>{displayCaption}</div>
                        )}
                    </div>
                    <button
                        type="button"
                        aria-label="Close image preview"
                        className={classes.closeBtn}
                        onClick={handleClose}
                    >
                        <CloseIcon />
                    </button>
                </div>
            )}
        </>
    );
};

export default PbdqImageLightboxWrapper;
